import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import { useTimeTrackingFunctions } from 'Context/TimeTracking';
import { Timesheets } from 'Components/TimeTracking';
import { listCompanyTimeCards, setRefreshCompanyTimeCards } from 'Containers/TimeTracking/actions';
import { TimeCardFilters } from 'Containers/TimeTracking/types';

import { refreshCompanyTimeCardsSelector } from 'Containers/TimeTracking/selectors';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';

type TabType = 'timesheets' | 'reports';

const TimesheetsContainer = () => {
  const dispatch = useDispatch();

  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);
  const refreshCompanyTimeCards = useSelector(refreshCompanyTimeCardsSelector, areEqual);
  const [activeTab, setActiveTab] = useState<TabType>('timesheets');
  const {
    savedEmployeeFilters,
    savedAddressFilters,
    savedProjectNumFilters,
    savedDateFilter,
    setIsCreateTimeCardModalOpen,
    setIsDownloadTimesheetsModalOpen,
    setIsManageTypesModalOpen,
  }: any = useTimeTrackingFunctions();

  const [initialPage, setInitialPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('-time_in');

  const getTimeCards = useCallback(
    (initialPage = 0) => {
      const filters: TimeCardFilters = {};
      if (savedDateFilter) {
        filters.startsBetween = savedDateFilter;
      }
      if (savedEmployeeFilters) {
        filters.employeeId = savedEmployeeFilters.toString();
      }
      if (savedAddressFilters) {
        filters.addressId = savedAddressFilters.toString();
      }
      if (savedProjectNumFilters) {
        filters.projectId = savedProjectNumFilters.toString();
      }

      dispatch(listCompanyTimeCards(firstCompanyId, filters, initialPage + 1, pageSize, sortBy));
    },
    [firstCompanyId, initialPage, pageSize, sortBy, savedEmployeeFilters, savedAddressFilters, savedProjectNumFilters, savedDateFilter]
  );

  const onPageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setInitialPage(0);
  }, []);

  // fetch whenever getTimeCards changes (covers filters, sort, pageSize, companyId)
  useEffect(() => {
    if (firstCompanyId) {
      setInitialPage(0);
      getTimeCards();
    }
  }, [getTimeCards]);

  // fetch on refresh (e.g. after create/edit/delete), then clear the flag so
  // the next create/edit/delete re-triggers it (it's a one-shot signal).
  useEffect(() => {
    if (refreshCompanyTimeCards) {
      getTimeCards();
      dispatch(setRefreshCompanyTimeCards(false));
    }
  }, [refreshCompanyTimeCards, getTimeCards, dispatch]);

  const onCreateTimeCardButtonClick = () => {
    setIsCreateTimeCardModalOpen(true);
  };

  const onDownloadButtonClick = () => {
    setIsDownloadTimesheetsModalOpen(true);
  };

  const onManageTypesButtonClick = () => {
    setIsManageTypesModalOpen(true);
  };

  return (
    <Timesheets
      getTimeCards={getTimeCards}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      onCreateTimeCardButtonClick={onCreateTimeCardButtonClick}
      onDownloadButtonClick={onDownloadButtonClick}
      onManageTypesButtonClick={onManageTypesButtonClick}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      sortBy={sortBy}
      onSortChange={setSortBy}
    />
  );
};

const TimesheetsContainerMemo = memo(TimesheetsContainer, areEqual);

export { TimesheetsContainerMemo as TimesheetsContainer };
